import api from "./apiClient";
import API_ENDPOINTS from "../config/apiEndpoints";
import { getApiTable, isApiSuccess } from "../components/Pages/qms/qmsApiUtils";

function isTruthyFlag(value) {
  return (
    value === true ||
    value === 1 ||
    String(value ?? "").trim() === "1" ||
    String(value ?? "").trim().toLowerCase() === "true"
  );
}

function extractOrgListRows(response) {
  const direct =
    getApiTable(response, "SuccessStory") ||
    getApiTable(response, "Table") ||
    getApiTable(response, "table");

  if (Array.isArray(direct) && direct.length > 0) {
    return direct;
  }

  const payload = response?.data ?? response?.Data ?? null;
  if (Array.isArray(payload)) return payload;

  if (payload && typeof payload === "object") {
    const tableValues = Object.values(payload).filter(Array.isArray);
    if (tableValues.length === 1) return tableValues[0];
  }

  return [];
}

/**
 * AQ Super Admin — list all customer organisations (EPlan_CalenderDefaultSetup sites).
 */
export async function fetchCustomerOrganisations() {
  const response = await api.get(
    API_ENDPOINTS.ORGANIZATION.GET_ORGANIZATIONS_DETAILS,
  );

  const rows = extractOrgListRows(response);

  return (rows || []).map((row) => ({
    organizationId: String(
      row.OrganizationId || row.organizationId || "",
    ).trim(),
    orgName: row.OrgName || row.orgName || "—",
    noOfUsers: Number(row.NoOfUsers ?? row.noOfUsers ?? 0),
    noOfTrials: Number(row.NoOfTrials ?? row.noOfTrials ?? 0),
    appointments: Number(row.Appointments ?? row.appointments ?? 0),
    volunteers: Number(row.Volunteers ?? row.volunteers ?? 0),
    totalSms: Number(row.TotalSMS ?? row.totalSms ?? 0),
    inactive: Number(row.InActive ?? row.inactive ?? 0) === 1,
    qmsTrainingAdditionalRolesEnabled: isTruthyFlag(
      row.QmsTrainingAdditionalRolesEnabled ??
        row.qmsTrainingAdditionalRolesEnabled,
    ),
    sponsorCroManagementEnabled: isTruthyFlag(
      row.SponsorCroManagementEnabled ?? row.sponsorCroManagementEnabled,
    ),
    mfaEnabled: isTruthyFlag(
      row.MfaEnabled ?? row.mfaEnabled ?? true,
    ),
    allowMultipleEPlannerReminders: isTruthyFlag(
      row.AllowMultipleEPlannerReminders ??
        row.allowMultipleEPlannerReminders,
    ),
    faqAiEnabled: isTruthyFlag(row.FaqAiEnabled ?? row.faqAiEnabled),
  }));
}

/**
 * AQ Super Admin — toggle QMS Training Additional Roles for one customer org.
 * Does not delete existing training-role rows when disabled.
 */
export async function saveOrgQmsTrainingRolesFlag({
  organizationId,
  enabled,
  createdBy,
}) {
  const response = await api.post(
    API_ENDPOINTS.ORGANIZATION.SUPER_ADMIN_QMS_TRAINING_FLAG,
    {
      OrganizationId: organizationId,
      Enabled: !!enabled,
      CreatedBy: createdBy || "",
      ActionType: "Save",
    },
  );

  if (!isApiSuccess(response)) {
    throw new Error(
      response?.message ||
        "Could not save Additional Training Roles flag.",
    );
  }

  const table = extractOrgListRows(response);
  const row = table.length > 0 ? table[0] : null;

  return {
    organizationId: String(
      row?.OrganizationId || organizationId || "",
    ).trim(),
    enabled: row
      ? isTruthyFlag(row.QmsTrainingAdditionalRolesEnabled)
      : !!enabled,
    message: response?.message || "Saved.",
  };
}

/**
 * AQ Super Admin — toggle Sponsor/CRO management for one customer org.
 */
export async function saveOrgSponsorCroManagementFlag({
  organizationId,
  enabled,
  createdBy,
}) {
  const response = await api.post(
    API_ENDPOINTS.ORGANIZATION.SUPER_ADMIN_SPONSOR_CRO_MANAGEMENT_FLAG,
    {
      OrganizationId: organizationId,
      Enabled: !!enabled,
      CreatedBy: createdBy || "",
      ActionType: "Save",
    },
  );

  if (!isApiSuccess(response)) {
    throw new Error(
      response?.message || "Could not save Sponsor/CRO management flag.",
    );
  }

  const table = extractOrgListRows(response);
  const row = table.length > 0 ? table[0] : null;

  return {
    organizationId: String(
      row?.OrganizationId || organizationId || "",
    ).trim(),
    enabled: row
      ? isTruthyFlag(
          row.SponsorCroManagementEnabled ?? row.sponsorCroManagementEnabled,
        )
      : !!enabled,
    message: response?.message || "Saved.",
  };
}

export async function fetchOrgFaqAiSettings({ organizationId, createdBy }) {
  const response = await api.post(API_ENDPOINTS.ORGANIZATION.SUPER_ADMIN_FAQ_AI_FLAG, {
    OrganizationId: organizationId,
    CreatedBy: createdBy || "",
    ActionType: "Get",
  });

  if (!isApiSuccess(response)) {
    throw new Error(response?.message || "Could not load FAQ AI settings.");
  }

  const table = extractOrgListRows(response);
  const row = table.length > 0 ? table[0] : null;
  const num = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  return {
    organizationId: String(row?.OrganizationId || organizationId || "").trim(),
    enabled: row ? isTruthyFlag(row.FaqAiEnabled ?? row.faqAiEnabled) : false,
    dailyOrgCap: num(row?.DailyOrgCap ?? row?.dailyOrgCap),
    dailyUserCap: num(row?.DailyUserCap ?? row?.dailyUserCap),
    message: response?.message || "OK",
  };
}

export async function saveOrgFaqAiFlag({
  organizationId,
  enabled,
  dailyOrgCap,
  dailyUserCap,
  createdBy,
}) {
  const response = await api.post(API_ENDPOINTS.ORGANIZATION.SUPER_ADMIN_FAQ_AI_FLAG, {
    OrganizationId: organizationId,
    Enabled: !!enabled,
    DailyOrgCap: dailyOrgCap,
    DailyUserCap: dailyUserCap,
    CreatedBy: createdBy || "",
    ActionType: "Save",
  });

  if (!isApiSuccess(response)) {
    throw new Error(response?.message || "Could not save FAQ AI settings.");
  }

  const table = extractOrgListRows(response);
  const row = table.length > 0 ? table[0] : null;
  const num = (value, fallback) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };

  return {
    organizationId: String(row?.OrganizationId || organizationId || "").trim(),
    enabled: row ? isTruthyFlag(row.FaqAiEnabled ?? row.faqAiEnabled) : !!enabled,
    dailyOrgCap: num(row?.DailyOrgCap ?? row?.dailyOrgCap, dailyOrgCap),
    dailyUserCap: num(row?.DailyUserCap ?? row?.dailyUserCap, dailyUserCap),
    message: response?.message || "Saved.",
  };
}

function parseFaqAiPlatformSettingsRow(row, fallback = {}) {
  const num = (value, defaultValue) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : defaultValue;
  };
  const flag = (value) =>
    isTruthyFlag(value) || String(value ?? "").trim() === "1";

  return {
    costInputPer1M: num(row?.CostInputPer1M ?? row?.costInputPer1M, fallback.costInputPer1M ?? 0.4),
    costOutputPer1M: num(row?.CostOutputPer1M ?? row?.costOutputPer1M, fallback.costOutputPer1M ?? 1.6),
    dailyOrgRequestCap: num(
      row?.DailyOrgRequestCap ?? row?.dailyOrgRequestCap,
      fallback.dailyOrgRequestCap ?? 50,
    ),
    dailyUserRequestCap: num(
      row?.DailyUserRequestCap ?? row?.dailyUserRequestCap,
      fallback.dailyUserRequestCap ?? 50,
    ),
    dailyCapMin: num(row?.DailyCapMin ?? row?.dailyCapMin, fallback.dailyCapMin ?? 1),
    dailyCapMax: num(row?.DailyCapMax ?? row?.dailyCapMax, fallback.dailyCapMax ?? 500),
    maxQuestionLength: num(
      row?.MaxQuestionLength ?? row?.maxQuestionLength,
      fallback.maxQuestionLength ?? 1000,
    ),
    maxQuestionLineBreaks: num(
      row?.MaxQuestionLineBreaks ?? row?.maxQuestionLineBreaks,
      fallback.maxQuestionLineBreaks ?? 6,
    ),
    retentionMonths: num(
      row?.RetentionMonths ?? row?.retentionMonths,
      fallback.retentionMonths ?? 18,
    ),
    azureBudgetAlertGbpMin: num(
      row?.AzureBudgetAlertGbpMin ?? row?.azureBudgetAlertGbpMin,
      fallback.azureBudgetAlertGbpMin ?? 20,
    ),
    azureBudgetAlertGbpMax: num(
      row?.AzureBudgetAlertGbpMax ?? row?.azureBudgetAlertGbpMax,
      fallback.azureBudgetAlertGbpMax ?? 50,
    ),
    environmentName: String(
      row?.EnvironmentName ?? row?.environmentName ?? fallback.environmentName ?? "",
    ).trim(),
    globalEnabled: flag(row?.GlobalEnabled ?? row?.globalEnabled ?? fallback.globalEnabled),
    azureEndpointSet: flag(
      row?.AzureEndpointSet ?? row?.azureEndpointSet ?? fallback.azureEndpointSet,
    ),
    azureKeySet: flag(row?.AzureKeySet ?? row?.azureKeySet ?? fallback.azureKeySet),
    azureDeployment: String(
      row?.AzureDeployment ?? row?.azureDeployment ?? fallback.azureDeployment ?? "",
    ).trim(),
    azureApiVersion: String(
      row?.AzureApiVersion ?? row?.azureApiVersion ?? fallback.azureApiVersion ?? "",
    ).trim(),
    updatedUtc: row?.UpdatedUtc ?? row?.updatedUtc ?? fallback.updatedUtc ?? "",
    updatedBy: String(row?.UpdatedBy ?? row?.updatedBy ?? fallback.updatedBy ?? "").trim(),
  };
}

export async function fetchFaqAiPlatformSettings({ createdBy }) {
  const response = await api.post(
    API_ENDPOINTS.ORGANIZATION.SUPER_ADMIN_FAQ_AI_PLATFORM_SETTINGS,
    {
      CreatedBy: createdBy || "",
      ActionType: "Get",
    },
  );

  if (!isApiSuccess(response)) {
    throw new Error(response?.message || "Could not load FAQ AI platform settings.");
  }

  const table = extractOrgListRows(response);
  const row = table.length > 0 ? table[0] : {};
  return {
    ...parseFaqAiPlatformSettingsRow(row),
    message: response?.message || "OK",
  };
}

export async function saveFaqAiPlatformSettings({ createdBy, ...settings }) {
  const response = await api.post(
    API_ENDPOINTS.ORGANIZATION.SUPER_ADMIN_FAQ_AI_PLATFORM_SETTINGS,
    {
      CreatedBy: createdBy || "",
      ActionType: "Save",
      CostInputPer1M: settings.costInputPer1M,
      CostOutputPer1M: settings.costOutputPer1M,
      DailyOrgRequestCap: settings.dailyOrgRequestCap,
      DailyUserRequestCap: settings.dailyUserRequestCap,
      DailyCapMin: settings.dailyCapMin,
      DailyCapMax: settings.dailyCapMax,
      MaxQuestionLength: settings.maxQuestionLength,
      MaxQuestionLineBreaks: settings.maxQuestionLineBreaks,
      RetentionMonths: settings.retentionMonths,
      AzureBudgetAlertGbpMin: settings.azureBudgetAlertGbpMin,
      AzureBudgetAlertGbpMax: settings.azureBudgetAlertGbpMax,
    },
  );

  if (!isApiSuccess(response)) {
    throw new Error(response?.message || "Could not save FAQ AI platform settings.");
  }

  const table = extractOrgListRows(response);
  const row = table.length > 0 ? table[0] : {};
  return {
    ...parseFaqAiPlatformSettingsRow(row, settings),
    message: response?.message || "Saved.",
  };
}

export async function fetchFaqAiUsage({ organizationId, fromUtc, toUtc, createdBy }) {
  const response = await api.post(API_ENDPOINTS.ORGANIZATION.GET_FAQ_AI_USAGE, {
    OrganizationId: organizationId || "",
    FromUtc: fromUtc || null,
    ToUtc: toUtc || null,
    CreatedBy: createdBy || "",
  });

  if (!isApiSuccess(response)) {
    throw new Error(response?.message || "Could not load FAQ AI usage.");
  }

  const payload = response?.data ?? response?.Data ?? {};
  const policy = payload.policy ?? payload.Policy ?? {};
  const summary = payload.summary ?? payload.Summary ?? {};
  const today = payload.today ?? payload.Today ?? {};
  const rows = extractOrgListRows(response);
  const num = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };

  return {
    rows: (rows || [])
      .filter((row) => row?.RequestId || row?.requestId || row?.Question || row?.question)
      .map((row) => ({
        requestId: String(row.RequestId || row.requestId || "").trim(),
        organizationId: String(row.OrganizationId || row.organizationId || "").trim(),
        orgName: row.OrgName || row.orgName || "",
        staffEmail: row.StaffEmail || row.staffEmail || "",
        staffName: row.StaffName || row.staffName || "",
        module: row.Module || row.module || "",
        question: row.Question || row.question || "",
        promptTokens: Number(row.PromptTokens ?? row.promptTokens ?? 0),
        completionTokens: Number(row.CompletionTokens ?? row.completionTokens ?? 0),
        totalTokens: Number(row.TotalTokens ?? row.totalTokens ?? 0),
        costGbp: Number(row.CostGbp ?? row.costGbp ?? 0),
        success: isTruthyFlag(row.Success ?? row.success),
        errorMessage: row.ErrorMessage || row.errorMessage || "",
        createdUtc: row.CreatedUtc || row.createdUtc || "",
      })),
    policy: {
      environmentName: String(
        policy.environmentName || policy.EnvironmentName || "",
      ).trim(),
      dailyOrgCap: num(policy.dailyOrgCap ?? policy.DailyOrgCap, 50),
      dailyUserCap: num(policy.dailyUserCap ?? policy.DailyUserCap, 50),
      dailyCapMin: num(policy.dailyCapMin ?? policy.DailyCapMin, 1),
      dailyCapMax: num(policy.dailyCapMax ?? policy.DailyCapMax, 500),
      maxQuestionLength: num(
        policy.maxQuestionLength ?? policy.MaxQuestionLength,
        1000,
      ),
      maxQuestionLineBreaks: num(
        policy.maxQuestionLineBreaks ?? policy.MaxQuestionLineBreaks,
        6,
      ),
      retentionMonths: num(policy.retentionMonths ?? policy.RetentionMonths, 18),
      azureBudgetAlertGbpMin: num(
        policy.azureBudgetAlertGbpMin ?? policy.AzureBudgetAlertGbpMin,
        20,
      ),
      azureBudgetAlertGbpMax: num(
        policy.azureBudgetAlertGbpMax ?? policy.AzureBudgetAlertGbpMax,
        50,
      ),
    },
    summary: {
      requestCount: num(summary.requestCount ?? summary.RequestCount, 0),
      failedCount: num(summary.failedCount ?? summary.FailedCount, 0),
      totalTokens: num(summary.totalTokens ?? summary.TotalTokens, 0),
      costGbp: num(summary.costGbp ?? summary.CostGbp, 0),
    },
    today: {
      orgUsed: num(today.orgUsed ?? today.OrgUsed, 0),
      orgCap: num(today.orgCap ?? today.OrgCap, 50),
      userCap: num(today.userCap ?? today.UserCap, 50),
    },
  };
}

/**
 * AQ Super Admin — list Sponsor/CRO study-access email templates for one org
 * (seeds defaults if missing).
 */
export async function fetchSponsorCroStudyAccessEmailTemplates(organizationId) {
  const response = await api.post(
    API_ENDPOINTS.ORGANIZATION.SUPER_ADMIN_SPONSOR_CRO_STUDY_ACCESS_EMAIL_TEMPLATE,
    {
      OrganizationId: organizationId,
      ActionType: "List",
    },
  );

  if (!isApiSuccess(response)) {
    throw new Error(
      response?.message || "Could not load study access email templates.",
    );
  }

  const rows = extractOrgListRows(response);
  return (rows || [])
    .filter((row) => row?.TemplateKey || row?.templateKey)
    .map((row) => ({
      templateKey: String(row.TemplateKey || row.templateKey || "").trim(),
      subjectTemplate: row.SubjectTemplate ?? row.subjectTemplate ?? "",
      bodyHtmlTemplate: row.BodyHtmlTemplate ?? row.bodyHtmlTemplate ?? "",
      bodyTextTemplate: row.BodyTextTemplate ?? row.bodyTextTemplate ?? "",
      isActive: isTruthyFlag(row.IsActive ?? row.isActive ?? true),
    }));
}

/**
 * AQ Super Admin — save one Sponsor/CRO study-access email template for an org.
 */
export async function saveSponsorCroStudyAccessEmailTemplate({
  organizationId,
  templateKey,
  subjectTemplate,
  bodyHtmlTemplate,
  bodyTextTemplate,
  updatedBy,
}) {
  const response = await api.post(
    API_ENDPOINTS.ORGANIZATION.SUPER_ADMIN_SPONSOR_CRO_STUDY_ACCESS_EMAIL_TEMPLATE,
    {
      OrganizationId: organizationId,
      TemplateKey: templateKey,
      SubjectTemplate: subjectTemplate,
      BodyHtmlTemplate: bodyHtmlTemplate,
      BodyTextTemplate: bodyTextTemplate || "",
      UpdatedBy: updatedBy || "",
      ActionType: "Save",
    },
  );

  if (!isApiSuccess(response)) {
    throw new Error(
      response?.message || "Could not save study access email template.",
    );
  }

  return {
    message: response?.message || "Template saved.",
  };
}

/**
 * AQ Super Admin — toggle MFA requirement for one customer org (default On).
 */
export async function saveOrgMfaFlag({ organizationId, enabled, createdBy }) {
  const response = await api.post(
    API_ENDPOINTS.ORGANIZATION.SUPER_ADMIN_MFA_FLAG,
    {
      OrganizationId: organizationId,
      Enabled: !!enabled,
      CreatedBy: createdBy || "",
      ActionType: "Save",
    },
  );

  if (!isApiSuccess(response)) {
    throw new Error(response?.message || "Could not save MFA flag.");
  }

  const table = extractOrgListRows(response);
  const row = table.length > 0 ? table[0] : null;

  return {
    organizationId: String(
      row?.OrganizationId || organizationId || "",
    ).trim(),
    enabled: row ? isTruthyFlag(row.MfaEnabled ?? true) : !!enabled,
    message: response?.message || "Saved.",
  };
}

function mapMessagingSettings(raw, organizationId) {
  const data = raw?.data ?? raw?.Data ?? raw ?? {};
  const countryIso2 = String(
    data.CountryIso2 || data.countryIso2 || "GB",
  )
    .trim()
    .toUpperCase() || "GB";
  let dialCode = String(data.DialCode || data.dialCode || "").trim();
  if (dialCode && !dialCode.startsWith("+")) {
    dialCode = `+${dialCode}`;
  }
  return {
    organizationId: String(
      data.OrganizationId || data.organizationId || organizationId || "",
    ).trim(),
    smsEnabled: isTruthyFlag(data.SmsEnabled ?? data.smsEnabled),
    whatsAppEnabled: isTruthyFlag(
      data.WhatsAppEnabled ?? data.whatsAppEnabled,
    ),
    hasTwilioSubaccount: isTruthyFlag(
      data.HasTwilioSubaccount ?? data.hasTwilioSubaccount,
    ),
    canSendSms: isTruthyFlag(data.CanSendSms ?? data.canSendSms),
    canSendWhatsApp: isTruthyFlag(
      data.CanSendWhatsApp ?? data.canSendWhatsApp,
    ),
    twilioAccountSid: String(
      data.TwilioAccountSid || data.twilioAccountSid || "",
    ).trim(),
    twilioMessagingServiceSid: String(
      data.TwilioMessagingServiceSid ||
        data.twilioMessagingServiceSid ||
        "",
    ).trim(),
    platformFromNumber: String(
      data.PlatformFromNumber || data.platformFromNumber || "",
    ).trim(),
    // Keep sms/wa fields equal to platform for any legacy UI reads — never prefer stale DB-only values.
    smsFromNumber: String(
      data.PlatformFromNumber ||
        data.platformFromNumber ||
        data.SmsFromNumber ||
        data.smsFromNumber ||
        "",
    ).trim(),
    whatsAppFromNumber: String(
      data.PlatformFromNumber ||
        data.platformFromNumber ||
        data.WhatsAppFromNumber ||
        data.whatsAppFromNumber ||
        "",
    ).trim(),
    countryIso2,
    dialCode: dialCode || "",
  };
}

/**
 * AQ Super Admin — toggle multiple e-Planner reminder points for one customer org.
 */
export async function saveOrgMultipleRemindersFlag({
  organizationId,
  enabled,
  createdBy,
}) {
  const response = await api.post(
    API_ENDPOINTS.ORGANIZATION.SUPER_ADMIN_MULTIPLE_REMINDERS_FLAG,
    {
      OrganizationId: organizationId,
      Enabled: !!enabled,
      CreatedBy: createdBy || "",
      ActionType: "Save",
    },
  );

  if (!isApiSuccess(response)) {
    throw new Error(
      response?.message || "Could not save multiple reminder points flag.",
    );
  }

  const table = extractOrgListRows(response);
  const row = table.length > 0 ? table[0] : null;

  return {
    organizationId: String(
      row?.OrganizationId || organizationId || "",
    ).trim(),
    enabled: row
      ? isTruthyFlag(
          row.AllowMultipleEPlannerReminders ??
            row.allowMultipleEPlannerReminders,
        )
      : !!enabled,
    message: response?.message || "Saved.",
  };
}

/**
 * AQ Super Admin — load Twilio SMS / WhatsApp settings for one org.
 */
export async function fetchOrgMessagingSettings(organizationId) {
  const response = await api.post(
    API_ENDPOINTS.ORGANIZATION.ORG_MESSAGING_SETTINGS,
    {
      OrganizationId: organizationId,
      ActionType: "Get",
    },
  );

  if (!isApiSuccess(response)) {
    throw new Error(
      response?.message || "Could not load messaging settings.",
    );
  }

  return mapMessagingSettings(response, organizationId);
}

/**
 * AQ Super Admin — create Twilio subaccount for one org (step 1).
 * Also creates/lists Messaging Service SID under that subaccount.
 */
export async function provisionOrgTwilioSubaccount({
  organizationId,
  createdBy,
}) {
  const response = await api.post(
    API_ENDPOINTS.ORGANIZATION.ORG_MESSAGING_SETTINGS,
    {
      OrganizationId: organizationId,
      ActionType: "Provision",
      CreatedBy: createdBy || "",
    },
  );

  if (!isApiSuccess(response)) {
    throw new Error(
      response?.message || "Could not create Twilio subaccount.",
    );
  }

  return {
    ...mapMessagingSettings(response, organizationId),
    message: response?.message || "Twilio subaccount provisioned.",
  };
}

/**
 * AQ Super Admin — clear local org SMS/WhatsApp settings (platform Twilio account kept).
 */
export async function deleteOrgTwilioSubaccount({ organizationId, createdBy }) {
  const response = await api.post(
    API_ENDPOINTS.ORGANIZATION.ORG_MESSAGING_SETTINGS,
    {
      OrganizationId: organizationId,
      ActionType: "ClearMessaging",
      CreatedBy: createdBy || "",
    },
  );

  if (!isApiSuccess(response)) {
    throw new Error(
      response?.message || "Could not clear SMS settings.",
    );
  }

  return {
    ...mapMessagingSettings(response, organizationId),
    message:
      response?.message ||
      "Local SMS/WhatsApp settings cleared for this organisation.",
  };
}

/**
 * AQ Super Admin — list/create Messaging Service (+ optional From number) for existing subaccount.
 */
export async function syncOrgTwilioMessaging({ organizationId, createdBy }) {
  const response = await api.post(
    API_ENDPOINTS.ORGANIZATION.ORG_MESSAGING_SETTINGS,
    {
      OrganizationId: organizationId,
      ActionType: "SyncMessaging",
      CreatedBy: createdBy || "",
    },
  );

  if (!isApiSuccess(response)) {
    throw new Error(
      response?.message || "Could not sync Messaging Service from Twilio.",
    );
  }

  return {
    ...mapMessagingSettings(response, organizationId),
    message: response?.message || "Messaging Service synced from Twilio.",
  };
}

/**
 * AQ Super Admin — save Twilio SMS / WhatsApp toggles and sender fields.
 */
export async function saveOrgMessagingSettings({
  organizationId,
  smsEnabled,
  whatsAppEnabled,
  twilioMessagingServiceSid,
  smsFromNumber,
  whatsAppFromNumber,
  createdBy,
}) {
  const response = await api.post(
    API_ENDPOINTS.ORGANIZATION.ORG_MESSAGING_SETTINGS,
    {
      OrganizationId: organizationId,
      ActionType: "Save",
      CreatedBy: createdBy || "",
      SmsEnabled: !!smsEnabled,
      WhatsAppEnabled: !!whatsAppEnabled,
      TwilioMessagingServiceSid: twilioMessagingServiceSid ?? "",
      SmsFromNumber: smsFromNumber ?? "",
      WhatsAppFromNumber: whatsAppFromNumber ?? "",
      ProvisionSubaccount: false,
    },
  );

  if (!isApiSuccess(response)) {
    throw new Error(
      response?.message || "Could not save messaging settings.",
    );
  }

  return {
    ...mapMessagingSettings(response, organizationId),
    message: response?.message || "Messaging settings saved.",
  };
}
